import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { resolveLabel } from '@/lib/fieldLabel';
import { CommonChartConfig, ReferenceLineInput, ToggleRow, SMALL_TRIGGER } from './sections';

export function BarChartConfig(props) {
  const { cfg, set, refLineValue, setRefLineValue, refLineLabel, setRefLineLabel, parameters } = props;
  return (
    <React.Fragment>
      <CommonChartConfig {...props} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Bar Layout</Label>
        <Select value={cfg.barLayout || 'grouped'} onValueChange={v => set('barLayout', v)}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="grouped">Grouped</SelectItem><SelectItem value="stacked">Stacked</SelectItem><SelectItem value="percent">100% Stacked</SelectItem></SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Orientation</Label>
        <Select value={cfg.orientation || 'vertical'} onValueChange={v => set('orientation', v)}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="vertical">Vertical</SelectItem><SelectItem value="horizontal">Horizontal</SelectItem></SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Bar Radius</Label>
        <Input value={cfg.barRadius || 4} onChange={e => set('barRadius', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-16" />
      </div>
      <ToggleRow label="Show Data Labels" checked={cfg.dataLabels || false} onChange={v => set('dataLabels', v)} />
      <ToggleRow label="Drill-down (hierarchy)" checked={cfg.drilldownHierarchy || false} onChange={v => set('drilldownHierarchy', v)} />
      <ReferenceLineInput refLineValue={refLineValue} setRefLineValue={setRefLineValue} refLineLabel={refLineLabel} setRefLineLabel={setRefLineLabel} parameters={parameters} />
    </React.Fragment>
  );
}

export function LineChartConfig(props) {
  const { cfg, set, refLineValue, setRefLineValue, refLineLabel, setRefLineLabel, parameters } = props;
  return (
    <React.Fragment>
      <CommonChartConfig {...props} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Line Style</Label>
        <Select value={cfg.lineStyle || 'solid'} onValueChange={v => set('lineStyle', v)}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="solid">Solid</SelectItem><SelectItem value="dashed">Dashed</SelectItem><SelectItem value="dotted">Dotted</SelectItem></SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Line Width</Label>
        <Input value={cfg.lineWidth || 2} onChange={e => set('lineWidth', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-16" />
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Curve Type</Label>
        <Select value={cfg.curveType || 'monotone'} onValueChange={v => set('curveType', v)}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="monotone">Smooth</SelectItem><SelectItem value="linear">Straight</SelectItem><SelectItem value="step">Stepped</SelectItem></SelectContent>
        </Select>
      </div>
      <ToggleRow label="Show Dots" checked={cfg.showDots !== false} onChange={v => set('showDots', v)} />
      <ToggleRow label="Drill-down (hierarchy)" checked={cfg.drilldownHierarchy || false} onChange={v => set('drilldownHierarchy', v)} />
      <ReferenceLineInput refLineValue={refLineValue} setRefLineValue={setRefLineValue} refLineLabel={refLineLabel} setRefLineLabel={setRefLineLabel} parameters={parameters} />
    </React.Fragment>
  );
}

export function AreaChartConfig(props) {
  const { cfg, set } = props;
  return (
    <React.Fragment>
      <CommonChartConfig {...props} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Fill Opacity</Label>
        <Input value={cfg.fillOpacity || 0.2} onChange={e => set('fillOpacity', Number(e.target.value))} type="number" step="0.1" min="0" max="1" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-16" />
      </div>
      <ToggleRow label="Stacked" checked={cfg.stacked || false} onChange={v => set('stacked', v)} />
      <ToggleRow label="Drill-down (hierarchy)" checked={cfg.drilldownHierarchy || false} onChange={v => set('drilldownHierarchy', v)} />
    </React.Fragment>
  );
}

export function PieChartConfig({ cfg, set, showLegend, setShowLegend }) {
  return (
    <React.Fragment>
      <ToggleRow label="Legend" checked={showLegend} onChange={setShowLegend} />
      <ToggleRow label="Donut (Inner Radius)" checked={cfg.donut !== false} onChange={v => set('donut', v)} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Inner Radius</Label>
        <Input value={cfg.innerRadius || 50} onChange={e => set('innerRadius', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-16" />
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Outer Radius</Label>
        <Input value={cfg.outerRadius || 100} onChange={e => set('outerRadius', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-16" />
      </div>
      <ToggleRow label="Show Labels" checked={cfg.showLabels || false} onChange={v => set('showLabels', v)} />
    </React.Fragment>
  );
}

export function ComboConfig(props) {
  const { cfg, set } = props;
  return (
    <React.Fragment>
      <CommonChartConfig {...props} />
      <p className="text-[8px] text-muted-foreground/70 italic">First measure as bars, remaining as lines</p>
      <ToggleRow label="Dual Y-Axis" checked={cfg.dualAxis || false} onChange={v => set('dualAxis', v)} />
    </React.Fragment>
  );
}

export function ScatterConfig(props) {
  const { cfg, set } = props;
  return (
    <React.Fragment>
      <CommonChartConfig {...props} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Dot Size</Label>
        <Input value={cfg.dotSize || 6} onChange={e => set('dotSize', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-16" />
      </div>
      <ToggleRow label="Show Trend Line" checked={cfg.trendLine || false} onChange={v => set('trendLine', v)} />
    </React.Fragment>
  );
}

export function GaugeConfig({ cfg, set, currentVisual, allFields, onUpdateVisual }) {
  return (
    <React.Fragment>
      <div>
        <Label className="text-[8px] text-muted-foreground">Gauge Measure</Label>
        <Select value={(currentVisual?.valueMeasures || [])[0] || ''} onValueChange={v => onUpdateVisual && onUpdateVisual({ valueMeasures: [v] })}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue placeholder="Select measure" /></SelectTrigger>
          <SelectContent>{(allFields || []).filter(f => f.semantic_type === 'measure').map(f => <SelectItem key={f.name} value={f.name} className="text-xs">{resolveLabel(allFields, f.name)}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-[7px] text-muted-foreground/70 mt-0.5">Or drag a measure to Gauge Value on the DATA tab</p>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Max Value</Label>
        <Input value={cfg.maxValue || 100} onChange={e => set('maxValue', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-20" />
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Warning Threshold</Label>
        <Input value={cfg.warningThreshold || 60} onChange={e => set('warningThreshold', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-20" />
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Danger Threshold</Label>
        <Input value={cfg.dangerThreshold || 80} onChange={e => set('dangerThreshold', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-20" />
      </div>
    </React.Fragment>
  );
}

export function FunnelConfig({ cfg, set }) {
  return (
    <React.Fragment>
      <ToggleRow label="Show Labels" checked={cfg.showLabels !== false} onChange={v => set('showLabels', v)} />
      <ToggleRow label="Show Percentages" checked={cfg.showPct || false} onChange={v => set('showPct', v)} />
    </React.Fragment>
  );
}

export function TreemapConfig({ cfg, set, themeActive }) {
  return (
    <React.Fragment>
      <ToggleRow label="Show Labels" checked={cfg.showLabels !== false} onChange={v => set('showLabels', v)} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Color Palette</Label>
        <Select value={cfg.palette || 'default'} onValueChange={v => set('palette', v)} disabled={themeActive}>
          <SelectTrigger className={SMALL_TRIGGER} data-testid="viz-color-palette-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="default">Default</SelectItem><SelectItem value="warm">Warm</SelectItem><SelectItem value="cool">Cool</SelectItem></SelectContent>
        </Select>
        {themeActive && <p className="text-[8px] text-muted-foreground/70 mt-0.5 leading-tight" data-testid="viz-color-palette-theme-hint">Controlled by the dashboard Color Theme. Set the theme to “Default (from config)” to use a per-visual palette.</p>}
      </div>
    </React.Fragment>
  );
}

export function HeatmapConfig({ cfg, set }) {
  return (
    <React.Fragment>
      <div>
        <Label className="text-[8px] text-muted-foreground">Color Scale</Label>
        <Select value={cfg.colorScale || 'blue'} onValueChange={v => set('colorScale', v)}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="blue">Blue</SelectItem><SelectItem value="red_green">Red &harr; Green</SelectItem><SelectItem value="purple">Purple</SelectItem></SelectContent>
        </Select>
      </div>
      <ToggleRow label="Show Values" checked={cfg.showValues || false} onChange={v => set('showValues', v)} />
    </React.Fragment>
  );
}

export function WordCloudConfig({ cfg, set }) {
  const from = cfg.colorFrom || '#6366f1';
  const to = cfg.colorTo || '#ef4444';
  const hasColorMeasure = !!cfg.colorMeasure;
  return (
    <React.Fragment>
      <ToggleRow label="Rotate some words" checked={cfg.wcRotate || false} onChange={v => set('wcRotate', v)} />
      <div className="pt-1">
        <Label className="text-[8px] text-muted-foreground">Color Gradient (low → high)</Label>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex flex-col items-center gap-0.5">
            <input type="color" value={from} onChange={e => set('colorFrom', e.target.value)}
              className="w-8 h-6 rounded border border-border/40 bg-transparent cursor-pointer p-0" data-testid="wordcloud-color-from" />
            <span className="text-[7px] text-muted-foreground">Low</span>
          </div>
          <div className="flex-1 h-6 rounded-md border border-border/40" style={{ background: `linear-gradient(90deg, ${from}, ${to})` }} data-testid="wordcloud-gradient-preview" />
          <div className="flex flex-col items-center gap-0.5">
            <input type="color" value={to} onChange={e => set('colorTo', e.target.value)}
              className="w-8 h-6 rounded border border-border/40 bg-transparent cursor-pointer p-0" data-testid="wordcloud-color-to" />
            <span className="text-[7px] text-muted-foreground">High</span>
          </div>
        </div>
        <p className="text-[8px] text-muted-foreground/70 mt-1 leading-snug">
          {hasColorMeasure
            ? `Words are colored from the low to the high color by "${cfg.colorMeasure}".`
            : 'Drop a measure into the "Color (Measure)" well on the DATA tab to color words low → high by that measure.'}
        </p>
      </div>
    </React.Fragment>
  );
}

export function WaterfallConfig(props) {
  const { cfg, set } = props;
  return (
    <React.Fragment>
      <CommonChartConfig {...props} />
      <ToggleRow label="Show Connectors" checked={cfg.connectors !== false} onChange={v => set('connectors', v)} />
      <ToggleRow label="Show Total Bar" checked={cfg.showTotal || false} onChange={v => set('showTotal', v)} />
    </React.Fragment>
  );
}

export function GeoMapConfig({ cfg, set }) {
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[8px] text-muted-foreground">Region Scope</Label>
        <Select value={cfg.geoScope || 'auto'} onValueChange={v => set('geoScope', v)}>
          <SelectTrigger className={SMALL_TRIGGER} data-testid="geo-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto-detect</SelectItem>
            <SelectItem value="us-states">US States</SelectItem>
            <SelectItem value="us-counties">US Counties</SelectItem>
            <SelectItem value="us-cities">US Cities</SelectItem>
            <SelectItem value="world">World (Countries)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Map Mode</Label>
        <Select value={cfg.geoMode || 'choropleth'} onValueChange={v => set('geoMode', v)}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="choropleth">Choropleth (Heat)</SelectItem><SelectItem value="points">Points (Bubbles)</SelectItem></SelectContent>
        </Select>
      </div>
      {(cfg.geoScope === 'us-counties' || cfg.geoScope === 'us-cities') && (
        <p className="text-[8px] text-muted-foreground/70 leading-snug">
          {cfg.geoScope === 'us-counties'
            ? 'Counties match by FIPS code or "County, ST". Use Choropleth for filled maps.'
            : 'Cities match by "City, ST". Use Points or Heat for city maps.'}
        </p>
      )}
    </div>
  );
}
